import axios from "axios";
import config, { ApiSystemData, ApiSystemExtraDetails, Regions, getConfigForRegion } from "../../config";
import { HeadersConfig } from "../../types/auth";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { systemsKeys } from "./queryKeys";
import { findRegionByDisplayName } from "./utils";

export interface SystemLogEntry {
    pusherMessage: string;
    creationTime: string;
}

export interface SystemLogsResponse {
    logs: SystemLogEntry[];
    numberOfPages: number;
    pagesLeft: number;
}

export interface SystemLogsParams {
    id: string;
    region: string;
    pageNumber?: number;
    log_type?: "LIVE_VIEW";
    like_query?: string;
}

export type SystemWithRegion = ApiSystemData & { region: string };

export const fetchAllSystems = async (headersWithAuth?: HeadersConfig): Promise<SystemWithRegion[]> => {
    const regions = Object.values(Regions);
    let allErrors = true;

    const regionFetches = regions.map(async (region) => {
        const regionConfig = getConfigForRegion(region);
        try {
            const response = await axios.post<ApiSystemData[]>(regionConfig.api.SYSTEMS, {}, headersWithAuth);
            allErrors = false;
            return response.data.map((system) => ({
                ...system,
                region: region.displayName
            }));
        } catch (error) {
            console.error(`[Systems ${region.displayName}] Error:`, error);
            return [];
        }
    });

    const results = await Promise.all(regionFetches);
    const allSystems = results.flat();

    if (allErrors && allSystems.length === 0) {
        throw new Error("Failed to fetch systems from all regions");
    }

    return allSystems;
};

export const useSystemsQuery = (headers?: HeadersConfig): UseQueryResult<SystemWithRegion[], Error> => {
    return useQuery<SystemWithRegion[]>({
        queryKey: systemsKeys.all,
        queryFn: () => fetchAllSystems(headers!),
        enabled: !!headers,
        staleTime: 1 * 60 * 1000,
        placeholderData: (prev) => prev,
        retry: 1
    });
};

export const useSystemExtraDetailsQuery = (systemId: string, region: string, headers?: HeadersConfig) => {
    return useQuery<ApiSystemExtraDetails, Error>({
        queryKey: ["systemExtraDetails", systemId, region],
        queryFn: async () => {
            try {
                const selectedRegion = findRegionByDisplayName(region);
                const regionConfig = getConfigForRegion(selectedRegion);

                const res = await axios.post<ApiSystemExtraDetails>(
                    regionConfig.api.SYSTEM_EXTRA_DETAILS,
                    { id: systemId },
                    headers
                );

                if (res.status !== 200 || !res.data) {
                    throw new Error("Failed to fetch system extra details");
                }

                return res.data;
            } catch (error) {
                console.error(`[System Details] Error fetching for system ${systemId} in ${region}:`, error);
                throw error;
            }
        },
        enabled: !!systemId && !!region && !!headers,
        retry: 1,
        refetchOnWindowFocus: false
    });
};

export const useSystemLogsQuery = (
    params: SystemLogsParams,
    headers?: HeadersConfig
): UseQueryResult<SystemLogsResponse, Error> => {
    const { id, region, pageNumber = 1, log_type } = params;

    return useQuery<SystemLogsResponse, Error>({
        queryKey: ["systemLogs", id, region, pageNumber, log_type],
        queryFn: async () => {
            try {
                const selectedRegion = findRegionByDisplayName(region);
                const regionConfig = getConfigForRegion(selectedRegion);

                const payload: any = { id };
                if (pageNumber) payload.pageNumber = pageNumber;
                if (log_type) payload.log_type = log_type;

                const response = await axios.post<SystemLogsResponse>(
                    regionConfig.api.SYSTEM_EXTRA_DETAILS.replace("/extra_details", "/pusher_logs"),
                    payload,
                    headers
                );

                return response.data;
            } catch (error) {
                console.error(`[System Logs] Error fetching logs for system ${id} in ${region}:`, error);
                throw error;
            }
        },
        enabled: !!id && !!region && !!headers,
        staleTime: 30 * 1000
    });
};

export const useSystemLiveViewLogsQuery = (
    params: Omit<SystemLogsParams, "log_type">,
    headers?: HeadersConfig
): UseQueryResult<SystemLogsResponse, Error> => {
    return useSystemLogsQuery({ ...params, log_type: "LIVE_VIEW" }, headers);
};

export interface LiveViewSession {
    startedAt: string;
    lastUpdate: string;
    durationSeconds: number;
    status: "ACTIVE" | "INACTIVE";
    userId: string;
    userEmail: string;
    systemId: string;
    systemName: string;
}
export interface LiveViewResponse {
    sessions: LiveViewSession[];
    numberOfPages: number;
    pagesLeft: number;
}

/** All live‐view sessions for a company */
export function useCompanyLiveViewSessionsQuery(
    companyId: number,
    page = 1,
    headers?: HeadersConfig
): UseQueryResult<LiveViewResponse, Error> {
    return useQuery<LiveViewResponse, Error>({
        queryKey: ["companyLV", companyId, page],
        queryFn: async () => {
            const { data } = await axios.post<LiveViewResponse>(
                config.api.COMPANY_LIVE_VIEW_SESSIONS,
                { id: companyId, pageNumber: page },
                headers
            );
            return data;
        },
        enabled: !!headers
    });
}

export function useSystemLiveViewSessionsQuery(
    systemId: string,
    page = 1,
    headers?: HeadersConfig
): UseQueryResult<LiveViewResponse, Error> {
    return useQuery<LiveViewResponse, Error>({
        queryKey: ["systemLV", systemId, page],
        queryFn: async () => {
            const { data } = await axios.post<LiveViewResponse>(
                config.api.SYSTEM_LIVE_VIEW_SESSIONS,
                { id: systemId, pageNumber: page },
                headers
            );
            return data;
        },
        enabled: !!headers
    });
}
